import { inBytes, inMB } from './file-sizes'
import { notNull } from '../utils'

export type MimeType = string
export type FileExtension = string

export interface FileConstraints {
   maxSizeBytes?: number
   typeMap?: FileAcceptMap
}

export type FileAcceptMap = Partial<
   Record<
      | MimeType
      | 'image/svg+xml'
      | 'audio/webm'
      | 'audio/ogg'
      | 'audio/mp4'
      | 'application/msword'
      | 'application/vnd.ms-excel'
      | 'text/csv'
      | 'text/html'
      | 'text/plain'
      | 'text/markdown',
      `.${FileExtension | 'doc' | 'xls' | 'jpeg' | 'svg' | 'webm' | 'ogg' | 'm4a' | 'csv' | 'htm' | 'html' | 'txt' | 'md'}`[]
   >
>

export type FilePurpose =
   | 'blueprintBlockContent'
   | 'processBlockImage'
   | 'processIcon'
   | 'logo'
   | 'RTEUploads'
   | 'importBackup'
   | 'proposalLogo'
   | 'proposalAttachment'
   | 'termsOfEngagement'
   | 'workItemAttachment'
   | 'workItemBlockUploads'
   | 'meetingAttachment'
   | 'inPersonAudio'
   | 'mindsets'
   | 'xpmSync'
   | 'markdown-files'
   | 'externalClientDocuments'

export const fileValidation: Record<FilePurpose, FileConstraints> = {
   blueprintBlockContent: {
      typeMap: {
         'image/png': ['.png'],
         'image/jpeg': ['.jpg', '.jpeg', '.png'],
      },
      maxSizeBytes: inBytes(10, 'MB'),
   },
   processBlockImage: {
      typeMap: {
         'image/png': ['.png'],
         'image/jpeg': ['.jpg', '.jpeg', '.png'],
      },
      maxSizeBytes: inBytes(10, 'MB'),
   },
   logo: {
      typeMap: {
         'image/png': ['.png'],
         'image/jpeg': ['.jpg', '.jpeg'],
         'image/svg+xml': ['.svg'],
         'application/xml': ['.svg'],
      },
      maxSizeBytes: inBytes(2, 'MB'),
   },
   processIcon: {},
   RTEUploads: {},
   proposalLogo: {
      typeMap: {
         'image/png': ['.png'],
         'image/jpeg': ['.jpg', '.jpeg', '.png'],
      },
      maxSizeBytes: inBytes(10, 'MB'),
   },
   proposalAttachment: {
      typeMap: {
         'application/pdf': ['.pdf'],
         'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.doc', '.docx'],
      },
      maxSizeBytes: inBytes(10, 'MB'),
   },
   termsOfEngagement: {
      typeMap: {
         'application/pdf': ['.pdf'],
      },
      maxSizeBytes: inBytes(10, 'MB'),
   },
   importBackup: {
      typeMap: {
         'application/zip': ['.zip'],
      },
   },
   workItemBlockUploads: {},
   workItemAttachment: {
      typeMap: {
         'application/pdf': ['.pdf'],
         'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.doc', '.docx'],
      },
      maxSizeBytes: inBytes(10, 'MB'),
   },
   meetingAttachment: {
      typeMap: {
         'application/pdf': ['.pdf'],
         'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.doc', '.docx'],
      },
      maxSizeBytes: inBytes(10, 'MB'),
   },
   inPersonAudio: {
      typeMap: {
         'audio/webm': ['.webm'],
         'video/webm': ['.webm'], // file-type detects audio-only WebM as video/webm (Matroska container)
         'audio/ogg': ['.ogg'],
         'audio/mp4': ['.m4a', '.mp4'],
      },
      maxSizeBytes: inBytes(500, 'MB'),
   },
   mindsets: {
      typeMap: {
         'image/png': ['.png'],
         'image/jpeg': ['.jpg', '.jpeg', '.png'],
         'image/gif': ['.gif'],
         'image/svg+xml': ['.svg'],
         'application/xml': ['.svg'],
      },
      maxSizeBytes: inBytes(3, 'MB'),
   },
   xpmSync: {
      maxSizeBytes: inBytes(10, 'MB'),
   },
   'markdown-files': {
      maxSizeBytes: inBytes(10, 'MB'),
   },
   // Keep extensions in sync with `supportedExtensions` in
   // backend/app-services/document-conversion/DocumentToMarkdownService.ts.
   externalClientDocuments: {
      typeMap: {
         'application/pdf': ['.pdf'],
         'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
         'application/msword': ['.doc'],
         'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
         'application/vnd.ms-excel': ['.xls'],
         'text/csv': ['.csv'],
         'text/html': ['.html', '.htm'],
         'text/plain': ['.txt'],
         'text/markdown': ['.md'],
      },
      maxSizeBytes: inBytes(4, 'MB'),
   },
}

export const fileValidationToDescription = (fileConstraint: FileConstraints) => {
   const extensions = [
      ...new Set(
         Object.values(fileConstraint.typeMap || {})
            .flat()
            .filter(Boolean)
      ),
   ]
   return `${extensions.map(extension => ' ' + extension!.slice(1).toUpperCase())} (max. ${inMB(fileConstraint.maxSizeBytes || 0, 'B')}MB)`
}

export const fileValidationDescription: Partial<Record<FilePurpose, string>> = {
   logo: fileValidationToDescription(fileValidation.logo),
   mindsets: fileValidationToDescription(fileValidation.mindsets),
}

export interface FileUploadProps {
   accept?: FileAcceptMap
   maxSize?: number
   subtext?: string
}

export const getFileUploadProps = (type: FilePurpose): FileUploadProps => {
   const validation = fileValidation[type]
   let acceptedExtentionText: string | undefined = undefined

   if (validation.typeMap && validation.maxSizeBytes) {
      acceptedExtentionText = `${Object.values(validation.typeMap || {})
         .map(x => x!.join(' '))
         .join(' ')} (max ${inMB(validation.maxSizeBytes, 'B')}MB)`
   }

   return {
      ...(validation.typeMap ? { accept: validation.typeMap } : {}),
      ...(validation.maxSizeBytes ? { maxSize: validation.maxSizeBytes } : {}),
      ...(acceptedExtentionText ? { subtext: acceptedExtentionText } : {}),
   }
}

/**
 * Precomputed upload props for every `FilePurpose`. Prefer this over calling
 * `getFileUploadProps(...)` at each call site so values are computed once at module load.
 */
export const fileUploadProps = Object.fromEntries(
   (Object.keys(fileValidation) as FilePurpose[]).map(purpose => [purpose, getFileUploadProps(purpose)])
) as Record<FilePurpose, FileUploadProps>

export const getFileValidationByObjectPurpose = (sourcePath: string[]) => {
   const pathObjectPurpose = getPathObjectPurpose(sourcePath)
   return fileValidation[notNull(pathObjectPurpose, `No support for upload path ${sourcePath.join('/')}`)]
}

/**
 * Infers the use of the object, given where we have stored it.
 * @param sourcePath An object key, split by `'/'`, e.g. `['unvalidated', '00gap', 'logo', 'logo.png']`
 */
export const getPathObjectPurpose = (sourcePath: string[]): FilePurpose | undefined => {
   const entity = sourcePath[2]
   const subEntity = sourcePath[4]

   switch (entity) {
      case 'work-items':
         // {orgId}/work-items/{workItemId}/attachments/{fileName} OR {orgId}/work-items/{workItemId}/block-uploads/{blockId}/{fileName}
         if (subEntity === 'attachments') {
            return 'workItemAttachment'
         }
         return 'workItemBlockUploads'
      case 'proposals':
         // {orgId}/proposals/{proposalId}/attachments/{fileName} OR {orgId}/proposals/{proposalId}/logo/{fileName}
         if (subEntity === 'attachments') {
            return 'proposalAttachment'
         }
         return 'proposalLogo'
      case 'terms-of-engagement':
         return 'termsOfEngagement'
      case 'blueprint-block-content':
         return 'blueprintBlockContent'
      case 'process-block-image':
         return 'processBlockImage'
      case 'logo':
         return 'logo'
      case 'RTEUploads':
         return 'RTEUploads'
      case 'importBackup':
         return 'importBackup'
      case 'process':
         return 'processIcon'
      case 'mindsets':
         return 'mindsets'
      case 'xpm':
         return 'xpmSync'
      case 'meetings':
         // {orgId}/meetings/{meetingId}/in-person-audio/{fileName} OR {orgId}/meetings/{meetingId}/attachments/{fileName}
         if (subEntity === 'in-person-audio') {
            return 'inPersonAudio'
         }
         return 'meetingAttachment'
      case 'markdown-files':
         return 'markdown-files'
      case 'external-client-documents':
         return 'externalClientDocuments'
      default:
         return undefined
   }
}

export const getOriginalFileName = (fileName: string): string => {
   // Strip ULID suffix if present, e.g. 'my_file-one-01HFSJ7JS77C2RE7N25068X7HA.pdf' -> 'my_file-one.pdf'
   const match = /^(.+)-([0-9a-z]{26})\.(.+)$/gi.exec(fileName)
   if (!match) return fileName

   const [, originalNamePart, , extension] = match
   return `${originalNamePart}.${extension}`
}

/**
 * Files stored in s3 can have spaces. The event hook adds "+" for some reason. This function filters those out (according to official documentation)
 * object.key sample before: unvalidated/01HFSJ7JS77C2RE7N25068X7HA/logo/Screenshot+2024-02-05+at+1.33.19+PM.png
 * object.key sample after:  unvalidated/01HFSJ7JS77C2RE7N25068X7HA/logo/Screenshot 2024-02-05 at 1.33.19 PM.png
 * @param key S3 object key
 */
export function formatObjectKey(key: string) {
   const sourcePath = key.split('/')
   const originalFileName = notNull(sourcePath.pop())
   const fileNameWithCorrectSpaces = originalFileName.replace(/\+/g, ' ')
   const formattedFileName = decodeURIComponent(fileNameWithCorrectSpaces)
   key = [...sourcePath, formattedFileName].join('/')
   return key
}
